import React, { useState, useEffect } from 'react';
import { Target, Users, Activity, Zap, RefreshCw, Layers, Crosshair, Shield, Map, Search, Server, CheckCircle, Package, Edit, Trash2, Clock, Compass } from 'lucide-react';
import { API_URL } from '../../config';

const Card = ({ children, className }) => <div className={`bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 ${className || ''}`}>{children}</div>;
const CardHeader = ({ children }) => <div className="p-6 pb-4 border-b border-slate-100 dark:border-slate-700/50">{children}</div>;
const CardTitle = ({ children }) => <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">{children}</h3>;
const CardDescription = ({ children }) => <p className="text-sm text-slate-500 mt-1">{children}</p>;
const CardContent = ({ children, className }) => <div className={`p-6 ${className || ''}`}>{children}</div>;

export default function IntentMatrix() {
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [projectsList, setProjectsList] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [seeds, setSeeds] = useState([]);
  
  const [isExpanding, setIsExpanding] = useState(false);
  const [expandedResults, setExpandedResults] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [strategyDepth, setStrategyDepth] = useState('L3'); // L1 to L5

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewFilter, setReviewFilter] = useState(null);
  const [flatPrompts, setFlatPrompts] = useState([]);
  const [isSaving, setIsSaving] = useState(false);


  useEffect(() => {
    fetchProjectData();
    
    const handleGlobalChange = () => {
      fetchProjectData();
    };
    window.addEventListener('globalFilterChanged', handleGlobalChange);
    return () => window.removeEventListener('globalFilterChanged', handleGlobalChange);
  }, []);

  const fetchProjectData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token') || '';
      const currentGlobalProjectId = localStorage.getItem('globalProject');
      
      const res = await fetch(`${API_URL}/brand-projects?system=geo`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      
      if (data.success && data.data && data.data.length > 0) {
        const activeProjects = data.data.filter(p => !p.status || p.status === "ACTIVE");
        setProjectsList(activeProjects);
        let targetProj = activeProjects.length > 0 ? activeProjects[0] : null;
        if (currentGlobalProjectId && currentGlobalProjectId !== 'all') {
           const matched = activeProjects.find(p => p.id === parseInt(currentGlobalProjectId));
           if (matched) {
               targetProj = matched;
           }
        }

        setProject(targetProj);
        setPersonas(targetProj.personas || []);
        // Map system keys to readable names
        const keyToName = {
          '__DIM_CATEGORY__': '品类探索 (Category)',
          '__DIM_NEEDS__': '核心需求 (Needs)',
          '__DIM_SCENARIOS__': '消费场景 (Scenarios)',
          '__DIM_BUDGETS__': '预算分层 (Budgets)',
          '__DIM_BRAND_PRODUCTS__': '本品主推单品',
          '__DIM_COMP_PRODUCTS__': '竞品对标单品'
        };

        const finalSeeds = (targetProj.themes && targetProj.themes.length > 0) 
          ? targetProj.themes 
          : (targetProj.PromptResearchSeed || []).map(seed => ({
              ...seed,
              name: keyToName[seed.name] || seed.name // Override with readable name if it's a known dimension
            }));
            
        setSeeds(finalSeeds);
        
        // First check for local draft to prevent generated matrix from disappearing on refresh
        const draft = localStorage.getItem(`intent_matrix_draft_${targetProj.id}`);
        if (draft) {
             try {
               const parsedDraft = JSON.parse(draft);
               setExpandedResults(parsedDraft.expanded);
               setFlatPrompts(parsedDraft.flatPrompts);
             } catch(e) {
               console.error("Failed to parse draft", e);
             }
        } else if (targetProj.keywordPackages && targetProj.keywordPackages.length > 0) {
           const latestPkg = targetProj.keywordPackages[targetProj.keywordPackages.length - 1];
           if (latestPkg.items && latestPkg.items.length > 0) {
              const groups = {};
              const flat = [];
              let idCounter = 0;
              
              latestPkg.items.forEach(item => {
                 const seedName = item.macro || '';
                 const personaName = item.stage || '';
                 
                 let kw = item.core || '';
                 let promptStr = item.expansions || kw;
                 const kwMatch = promptStr.match(/\] (.+)/);
                 if (kwMatch) {
                    promptStr = kwMatch[1].trim();
                 }
                 
                 const key = `${seedName}_${personaName}`;
                 if (!groups[key]) {
                    groups[key] = { seed: seedName, persona: personaName, heat: item.volume || 0, strategy: '已归档词包', prompts: [] };
                 }
                 
                 const pObj = { kw, prompt: promptStr };
                 groups[key].prompts.push(pObj);
                 
                 flat.push({
                    id: `saved_${idCounter++}`,
                    seed: seedName,
                    persona: personaName,
                    kw,
                    prompt: promptStr,
                    heat: item.volume || 0,
                    strategy: '已归档词包'
                 });
              });
              
              setExpandedResults(Object.values(groups));
              setFlatPrompts(flat);
              setPackageId(latestPkg.id);
           }
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  
  const handleSaveMatrix = async () => {
    setIsSaving(true);
    try {
       const token = localStorage.getItem('token') || '';
       
       // Reconstruct expandedData from flatPrompts
       const groups = {};
       flatPrompts.forEach(item => {
          const key = `${item.seed}_${item.persona}`;
          if (!groups[key]) groups[key] = { seed: item.seed, persona: item.persona, heat: item.heat, strategy: item.strategy, prompts: [] };
          groups[key].prompts.push({ kw: item.kw, prompt: item.prompt });
       });
       
       const expandedData = Object.values(groups);
       
       const res = await fetch(`${API_URL}/seo/config/geo-ai-save-matrix`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
           'Authorization': `Bearer ${token}`
         },
         body: JSON.stringify({ projectId: project.id, expandedData })
       });
       
       const data = await res.json();
       if (data.success) {
          setPackageId(data.packageId);
          setShowReviewModal(false);
          localStorage.removeItem(`intent_matrix_draft_${project.id}`);
       } else {
          alert(data.error || '保存失败');
       }
    } catch (e) {
       console.error(e);
       alert('网络错误');
    } finally {
       setIsSaving(false);
    }
  };

  const handleExpand = async (force = false) => {
    if (!project || seeds.length === 0) return;
    
    setIsExpanding(true);
    if (force) {
      setExpandedResults(null);
      setFlatPrompts([]);
    }
    setPackageId(null);
    try {
      const token = localStorage.getItem('token') || '';
      
      let allDone = false;
      let loopCount = 0;
      let currentExpandedResults = (!force && expandedResults) ? [...expandedResults] : [];

      while (!allDone && loopCount < 10) {
          loopCount++;
          
          let activeP = personas && personas.length > 0 ? [...personas] : [{name: '普通大众'}];
          if (!activeP.find(p => p.name === '普通大众')) activeP.unshift({name: '普通大众'});
          if (strategyDepth === 'L1' || strategyDepth === 'L2') activeP = [{name: '普通大众'}];
          const activePNames = activeP.map(p => p.name);

          const existingSeeds = [];
          const seedToPersonas = {};
          if (currentExpandedResults) {
              currentExpandedResults.forEach(e => {
                  if (!seedToPersonas[e.seed]) seedToPersonas[e.seed] = new Set();
                  seedToPersonas[e.seed].add(e.persona);
              });
              for (const [seed, pSet] of Object.entries(seedToPersonas)) {
                  if (activePNames.every(p => pSet.has(p))) {
                      existingSeeds.push(seed);
                  }
              }
          }
          
          const payload = {
            brandName: project.name,
            seeds: seeds,
            personas: personas,
            strategyDepth: strategyDepth,
            projectId: project.id,
            existingSeeds: existingSeeds,
            batchSize: 1 // Tell backend to only process 1 seed per request to prevent token limit
          };

          const res = await fetch(`${API_URL}/seo/config/geo-ai-expand`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });
          
          const resData = await res.json();
          
          if (resData.success && resData.data && resData.data.expanded && resData.data.expanded.length > 0) {
               // Merge with existing expandedResults
               const existing = [...currentExpandedResults];
               resData.data.expanded.forEach(newExp => {
                  const existingIdx = existing.findIndex(e => e.seed === newExp.seed && e.persona === newExp.persona);
                  if (existingIdx >= 0) {
                     existing[existingIdx] = newExp; 
                  } else {
                     existing.push(newExp);
                  }
               });
               currentExpandedResults = existing;
               
               // Update UI progressively!
               setExpandedResults([...currentExpandedResults]);
          } else if (resData.error) {
               console.error("Expand API Error:", resData.error);
               allDone = true; // Stop loop on error
               alert(`大模型生成失败: ${resData.error}`);
          } else {
               // Nothing more to generate
               allDone = true;
          }
      }

      // Final flatten for review table after all batches
      const flat = [];
      currentExpandedResults.forEach(exp => {
         (exp.prompts || []).forEach((pObj, idx) => {
            flat.push({
               id: `${exp.seed}_${exp.persona}_${idx}`,
               seed: exp.seed,
               persona: exp.persona,
               kw: pObj.kw,
               prompt: pObj.prompt,
               heat: exp.heat || 0,
               strategy: exp.strategy || ''
            });
         });
      });
      setFlatPrompts(flat);
      
      // Save to localStorage as draft
      localStorage.setItem(`intent_matrix_draft_${project.id}`, JSON.stringify({
        expanded: currentExpandedResults,
        flatPrompts: flat
      }));
           
    } catch (e) {
      console.error(e);
    } finally {
      setIsExpanding(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 max-w-7xl mx-auto flex items-center justify-center min-h-[60vh]">
         <div className="flex flex-col items-center">
            <Activity className="w-10 h-10 text-blue-500 animate-spin mb-4" />
            <span className="text-slate-500 font-medium">同步引擎势能中...</span>
         </div>
      </div>
    );
  }

  const hasSeeds = seeds.length > 0;
  const isQuickMode = true; // Temporary force-bypass for Prime Quick Mode until ExpansionTask flow is standardized
  const hasCompletedExpansion = isQuickMode || project?.expansionTasks?.some(t => t.status === 'COMPLETED' && t.expandedData);
  const isReadyToFuse = hasSeeds && hasCompletedExpansion;
  
  // Matrix UI Splitting Logic: Filter visible grid to specifically match the current strategy depth
  let activePersonas = personas && personas.length > 0 ? [...personas] : [{name: '普通大众', prompt: '无差别泛搜人群'}];
  // Ensure '普通大众' is always first
  if (!activePersonas.find(p => p.name === '普通大众')) {
      activePersonas.unshift({name: '普通大众', prompt: '无差别泛搜人群'});
  }
  
  if (strategyDepth === 'L1' || strategyDepth === 'L2') {
     activePersonas = [{name: '普通大众', prompt: '无差别泛搜人群'}];
  }
  
  let activeSeeds = seeds;
  if (strategyDepth === 'L1') {
    activeSeeds = seeds.filter(s => s.name.includes('Category') || s.name.includes('品类') || s.name.includes('__DIM_CATEGORY__') || s.name.includes('本品') || s.name.includes('__DIM_BRAND_PRODUCTS__') || s.name.includes('竞品') || s.name.includes('__DIM_COMP_PRODUCTS__'));
  } else if (strategyDepth === 'L2' || strategyDepth === 'L3') {
    activeSeeds = seeds.filter(s => s.name.includes('Needs') || s.name.includes('需求') || s.name.includes('__DIM_NEEDS__'));
  } else if (strategyDepth === 'L4') {
    activeSeeds = seeds.filter(s => s.name.includes('Scenarios') || s.name.includes('场景') || s.name.includes('__DIM_SCENARIOS__') || (!s.name.includes('Category') && !s.name.includes('__DIM_CATEGORY__') && !s.name.includes('本品') && !s.name.includes('__DIM_BRAND_PRODUCTS__') && !s.name.includes('竞品') && !s.name.includes('__DIM_COMP_PRODUCTS__') && !s.name.includes('Needs') && !s.name.includes('需求') && !s.name.includes('__DIM_NEEDS__') && !s.name.includes('Budgets') && !s.name.includes('预算') && !s.name.includes('__DIM_BUDGETS__')));
  } else if (strategyDepth === 'L5') {
    activeSeeds = seeds.filter(s => s.name.includes('Budgets') || s.name.includes('预算') || s.name.includes('__DIM_BUDGETS__') || s.name.includes('Scenarios') || s.name.includes('场景') || s.name.includes('__DIM_SCENARIOS__'));
  }

  const pList = activePersonas;
  
  const levelExplanations = {
      'L1': { title: '泛品类觉醒 (L1)', desc: '当前网格仅展示【品类词、本品词、竞品词】，针对普通大众，拦截行业最大流向。' },
      'L2': { title: '痛点与需求 (L2)', desc: '当前网格仅展示【核心需求词】，针对普通大众，过滤带有明确功能诉求的意图。' },
      'L3': { title: '精细人群心智 (L3)', desc: '当前网格展示【核心需求词】与【所有人设】的交叉，针对特定群体种草。' },
      'L4': { title: '生活场景触发 (L4)', desc: '当前网格展示【消费场景词】与【所有人设】的交叉，结合痛点直击使用场景。' },
      'L5': { title: '全维终极决策 (L5)', desc: '当前网格展示【预算分层词】与【所有人设】的交叉，融合场景与价格，拦截终极防线。' }
  };
  const currentExplanation = levelExplanations[strategyDepth];

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
            <Map className="w-8 h-8 text-blue-500" />
            意图作战网格 (Intent Matrix)
          </h1>
          <p className="text-slate-500 mt-2">基于全局势能引擎驱动的 AIO 战略沙盘，实现从心智探索到精准反制的闭环。</p>
        </div>
        <div className="flex gap-3">
          
          <div className="relative">
             <select 
               className="px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-lg shadow-sm hover:bg-slate-50 appearance-none pr-8 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500"
               value={project ? project.id : ''}
               onChange={(e) => {
                 const id = e.target.value;
                 localStorage.setItem('globalProject', id);
                 window.dispatchEvent(new Event('globalFilterChanged'));
               }}
             >
               <option value="" disabled>选择主体任务...</option>
               {projectsList.map(p => (
                 <option key={p.id} value={p.id}>当前主体: {p.name} (ID: {p.id})</option>
               ))}
             </select>
             <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
               <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
             </div>
          </div>

          <div className="flex items-center gap-2">
            <button 
               onClick={() => handleExpand(false)}
               disabled={isExpanding || !isReadyToFuse}
               className="px-4 py-2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-lg shadow-md hover:from-blue-700 hover:to-cyan-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
               {isExpanding ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Crosshair className="w-4 h-4" />}
               {isExpanding ? '矩阵膨胀中...' : '大模型核聚变 (生成矩阵)'}
            </button>
            <button 
               onClick={() => {
                 if (window.confirm('警告：这将会清空当前网格上已有生成的所有意图词，并强制大模型重新生成一遍。确定要继续吗？')) {
                   handleExpand(true);
                 }
               }}
               disabled={isExpanding || !isReadyToFuse}
               title="清空当前已有矩阵并强制重新生成"
               className="px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-lg shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 text-sm font-medium">
               强制重刷
            </button>
          </div>
        </div>
      </div>

      <div className="mb-6 bg-white dark:bg-slate-800 p-4 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Layers className="w-5 h-5 text-indigo-500" />
          <span className="font-semibold text-slate-800 dark:text-slate-200">战略纵深拨盘 (L1 - L5)</span>
          <span className="text-xs text-slate-500 bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">控制长尾裂变深度</span>
        </div>
        <div className="flex bg-slate-100 dark:bg-slate-900 p-1 rounded-lg">
          {[
            { id: 'L1', title: '泛品类觉醒', desc: '最大流量口，拦截行业基础品类。', ex: '海蓝之谜面霜、有什么好用的面霜' },
            { id: 'L2', title: '痛点与需求', desc: '叠加功能诉求，过滤带明确意图的搜索。', ex: '海蓝之谜抗老效果、抗皱面霜推荐' },
            { id: 'L3', title: '精细人群心智', desc: '带上受众人设，针对特定群体种草。', ex: '敏感肌能不能用海蓝之谜' },
            { id: 'L4', title: '生活场景触发', desc: '结合送礼、换季等高转化具体场景。', ex: '送丈母娘的抗老面霜推荐' },
            { id: 'L5', title: '全维终极决策', desc: '加入预算限制，直逼成交的最长尾词。', ex: '3000元以内送老婆的抗老修护面霜' }
          ].map(level => (
            <div key={level.id} className="relative group">
              <button
                onClick={() => setStrategyDepth(level.id)}
                className={`px-4 py-2 text-sm font-bold rounded-md transition-all ${strategyDepth === level.id ? 'bg-indigo-500 text-white shadow-md' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-800'}`}
              >
                {level.id}
              </button>
              
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 bg-slate-800 dark:bg-slate-700 text-white p-3 rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none border border-slate-700 dark:border-slate-600">
                <div className="font-bold text-indigo-400 mb-1 flex items-center gap-2">
                  <span>{level.id}</span>
                  <span className="text-white text-sm">{level.title}</span>
                </div>
                <div className="text-xs text-slate-300 leading-relaxed mb-2">{level.desc}</div>
                <div className="text-xs text-slate-400 bg-slate-900/50 p-2 rounded border border-slate-700/50">
                  <span className="text-slate-500">示例: </span>"{level.ex}"
                </div>
                <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800 dark:border-t-slate-700"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-50 to-white dark:from-slate-800 dark:to-slate-900 border-blue-100 dark:border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
               <Shield className="w-5 h-5 text-blue-600" />
               <h3 className="font-semibold text-blue-900 dark:text-blue-100">核心意图 (Y轴)</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">当前纵深模式下的有效维度。</p>
            <div className="mt-4 text-2xl font-bold text-blue-700 dark:text-blue-400">{activeSeeds.length} 个意图</div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-white dark:from-slate-800 dark:to-slate-900 border-purple-100 dark:border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
               <Users className="w-5 h-5 text-purple-600" />
               <h3 className="font-semibold text-purple-900 dark:text-purple-100">受众人设 (X轴)</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">当前纵深模式下的人群颗粒度。</p>
            <div className="mt-4 text-2xl font-bold text-purple-700 dark:text-purple-400">{pList.length} 个人设</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-50 to-white dark:from-slate-800 dark:to-slate-900 border-rose-100 dark:border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 mb-2">
               <Target className="w-5 h-5 text-rose-600" />
               <h3 className="font-semibold text-rose-900 dark:text-rose-100">矩阵当量</h3>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400">核聚变产生的长尾探针总计预估。</p>
            <div className="mt-4 text-2xl font-bold text-rose-700 dark:text-rose-400">{activeSeeds.length * pList.length * 3}+ 条探针</div>
          </CardContent>
        </Card>
      </div>
      
      {!hasSeeds && (
        <Card>
          <CardContent className="p-12 text-center">
            <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-500">网格初始化中...</h3>
            <p className="text-sm text-slate-400 mt-2">未能检测到项目意图数据，请先返回「项目向导」配置思 PRIME 种子词。</p>
          </CardContent>
        </Card>
      )}

      {hasSeeds && !hasCompletedExpansion && (
        <Card>
          <CardContent className="p-12 text-center">
            <Clock className="w-12 h-12 text-amber-500 mx-auto mb-4 animate-pulse" />
            <h3 className="text-lg font-medium text-slate-500">等待拓词数据返回...</h3>
            <p className="text-sm text-slate-400 mt-2">后台工作人员正在进行百度和小红书的搜索量跑取与拓展词挖掘，请耐心等待返回数据后，再启动大模型核聚变。</p>
          </CardContent>
        </Card>
      )}

      {hasSeeds && hasCompletedExpansion && (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            
            {currentExplanation && (
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border-l-4 border-indigo-500 p-4 mb-4 mx-4 mt-4 rounded-r-lg">
                 <h4 className="text-sm font-bold text-indigo-800 dark:text-indigo-300">{currentExplanation.title} 视图说明</h4>
                 <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">{currentExplanation.desc}</p>
              </div>
            )}
            <table className="w-full text-left border-collapse">
              <thead>
                <tr>
                  <th className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80 font-semibold text-slate-700 dark:text-slate-300 w-48 sticky left-0 z-10 border-r">
                    <div className="flex items-center gap-2">
                      <Search className="w-4 h-4 text-slate-400" /> 意图与场景
                    </div>
                  </th>
                  {pList.map((p, idx) => (
                    <th key={idx} className="p-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/40 font-medium text-slate-600 dark:text-slate-300 min-w-[300px]">
                      <div className="text-sm text-blue-600 dark:text-blue-400 font-bold mb-1">{p.name}</div>
                      <div className="text-xs text-slate-400 font-normal truncate max-w-[280px]" title={p.prompt}>{p.prompt || '暂无描述'}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {activeSeeds.map((s, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors">
                    <td className="p-4 align-top border-r border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 sticky left-0 z-10">
                      <div className="font-semibold text-slate-800 dark:text-white text-sm mb-1">{s.name}</div>
                      <div className="text-xs text-slate-500 leading-relaxed max-w-[160px] truncate" title={s.keywords}>{s.keywords}</div>
                    </td>
                    {pList.map((p, colIndex) => {
                      // 查找对应的生成结果
                      // Handle both flat and nested JSON responses from LLM (sometimes DeepSeek returns extra wrapping or slight naming variations)
                      let resList = expandedResults;
                      if (expandedResults && !Array.isArray(expandedResults) && Array.isArray(expandedResults.expanded)) {
                         resList = expandedResults.expanded;
                      }
                      
                      const res = Array.isArray(resList) ? resList.find(er => 
                         (er.seed === s.name || (er.seed && er.seed.includes(s.name)) || (s.name && s.name.includes(er.seed))) && 
                         (er.persona === p.name || (er.persona && er.persona.includes(p.name)) || (p.name && p.name.includes(er.persona)))
                      ) : null;
                      
                      return (
                        
                        
                        <td key={colIndex} className="p-0 relative group border border-slate-100 dark:border-slate-700/50">
                          {isExpanding && !expandedResults && (
                            <div className="absolute inset-0 flex items-center justify-center bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur-[1px]">
                              <RefreshCw className="w-5 h-5 text-indigo-500 animate-spin" />
                            </div>
                          )}
                          {!isExpanding && !expandedResults && (
                            <div className="h-28 m-2 rounded-lg border border-dashed border-slate-200 dark:border-slate-700 flex flex-col items-center justify-center text-xs text-slate-400 opacity-50 group-hover:opacity-100 transition-opacity bg-slate-50/30 dark:bg-slate-800/30">
                              <Server className="w-4 h-4 mb-1" />
                              <span>待部署火力</span>
                            </div>
                          )}
                          {res && (
                            <div className={`h-full min-h-[110px] m-1 rounded-lg border p-3 flex flex-col justify-between transition-all duration-300 ${
                               res.heat > 5000 ? 'bg-orange-50/80 border-orange-200 dark:bg-orange-900/20 dark:border-orange-800/50 hover:bg-orange-100' :
                               res.heat > 1000 ? 'bg-amber-50/80 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800/50 hover:bg-amber-100' :
                               'bg-blue-50/80 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800/50 hover:bg-blue-100'
                            }`}>
                               <div className="flex justify-between items-start mb-2">
                                  <div className="flex flex-col flex-1">
                                     <span className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1">
                                       <Target className="w-3 h-3 text-indigo-500 flex-shrink-0" /> <span className="truncate max-w-[100px]">{res.seed}</span>
                                     </span>
                                     <span className="text-[10px] text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1" title={res.strategy || '种草型'}>
                                       {res.strategy || '种草型'}
                                     </span>
                                  </div>
                                  <div className="flex flex-col items-end gap-1">
                                     <div className={`flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                       res.heat > 5000 ? 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30' :
                                       res.heat > 1000 ? 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/30' :
                                       'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30'
                                     }`}>
                                        <Zap className="w-3 h-3" />
                                        {res.heat > 1000 ? (res.heat/1000).toFixed(1) + 'k' : (res.heat || 0)}
                                     </div>
                                  </div>
                               </div>

                               <div className="flex gap-2 mb-2 px-1">
                                  {res.xhsHeat > 0 && <span className="text-[10px] text-red-600/80 dark:text-red-400/80">小红书: {res.xhsHeat > 1000 ? (res.xhsHeat/1000).toFixed(1)+'k' : res.xhsHeat}</span>}
                                  {res.bdHeat > 0 && <span className="text-[10px] text-blue-600/80 dark:text-blue-400/80">百度: {res.bdHeat > 1000 ? (res.bdHeat/1000).toFixed(1)+'k' : res.bdHeat}</span>}
                               </div>
                               
                               {res.mappedKeywords && res.mappedKeywords.length > 0 && (
                                 <div className="text-[10px] text-slate-600 dark:text-slate-400 mb-2 px-1 truncate">
                                    拓词: {res.mappedKeywords.slice(0,2).map(k => k.keyword).join(', ')} {res.mappedKeywords.length > 2 ? '...' : ''}
                                 </div>
                               )}

                               <div className="mt-auto pt-2 border-t border-black/5 dark:border-white/5 flex items-center justify-between text-xs">
                                  <span className="text-slate-600 dark:text-slate-400 font-medium">
                                    <Layers className="w-3 h-3 inline mr-1 opacity-70" /> {res.totalCount} 条指令
                                  </span>
                                  <button onClick={() => { setReviewFilter({seed: s.name, persona: p.name}); setShowReviewModal(true); }} className="text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 font-semibold cursor-pointer z-10">
                                    审阅
                                  </button>
                               </div>

                               {/* Hover Preview Panel */}
                               <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-72 bg-slate-800 dark:bg-slate-900 text-white text-xs rounded-lg shadow-xl p-3 opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-200 z-50">
                                  <div className="font-semibold text-slate-300 border-b border-slate-700 pb-1 mb-2 flex justify-between">
                                     <span>指令预览 (前2条)</span>
                                     <span className="text-emerald-400">{res.heat} 流量指数</span>
                                  </div>
                                  
                                  {res.mappedKeywords && res.mappedKeywords.length > 0 && (
                                     <div className="mb-2 text-slate-400 leading-relaxed">
                                        <span className="text-slate-300 font-medium">注入的反哺拓词：</span>
                                        {res.mappedKeywords.map(k => k.keyword).join(', ')}
                                     </div>
                                  )}
                                  
                                  <div className="space-y-1.5 text-slate-200 line-clamp-4">
                                     {(res.displayPrompts || res.prompts || []).slice(0, 2).map((pObj, i) => (
                                        <div key={i} className="bg-slate-700/50 rounded px-2 py-1 truncate">
                                          {typeof pObj === 'string' ? pObj : pObj.prompt}
                                        </div>
                                     ))}
                                  </div>
                                  <div className="absolute left-1/2 -bottom-1 -translate-x-1/2 border-4 border-transparent border-t-slate-800 dark:border-t-slate-900"></div>
                               </div>
                            </div>
                          )}
                        </td>


                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      
      {showReviewModal && (() => {
        const displayPrompts = reviewFilter ? flatPrompts.filter(p => {
          const matchSeed = p.seed === reviewFilter.seed || (p.seed && p.seed.includes(reviewFilter.seed)) || (reviewFilter.seed && p.seed && reviewFilter.seed.includes(p.seed));
          const matchPersona = p.persona === reviewFilter.persona || (p.persona && p.persona.includes(reviewFilter.persona)) || (reviewFilter.persona && p.persona && reviewFilter.persona.includes(p.persona));
          return matchSeed && matchPersona;
        }) : flatPrompts;
        return (
        <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl w-full max-w-5xl h-[85vh] border border-gray-100 dark:border-slate-700 flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 flex justify-between items-center bg-gray-50/50 dark:bg-slate-900/20">
              <h3 className="font-semibold text-gray-900 dark:text-slate-100 flex items-center">
                <Edit className="mr-2 text-indigo-500 w-5 h-5" /> {reviewFilter ? `审核局部网格 [${reviewFilter.seed} × ${reviewFilter.persona}]` : '审核与修改全表'} ({displayPrompts.length} 条)
              </h3>
              <button onClick={() => setShowReviewModal(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300 transition-colors">&times;</button>
            </div>
            
            <div className="flex-1 overflow-auto bg-slate-50 dark:bg-slate-900/30 p-6 custom-scrollbar">
               <table className="w-full text-left text-sm whitespace-nowrap bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700">
                 <thead className="bg-slate-100 dark:bg-slate-900/50 text-slate-600 dark:text-slate-400 sticky top-0 z-10 shadow-sm">
                   <tr>
                     <th className="px-4 py-3 font-medium w-32">业务维度 (Seed)</th>
                     <th className="px-4 py-3 font-medium w-32">受众人设 (Persona)</th>
                     <th className="px-4 py-3 font-medium w-32">核心词 (Keyword)</th>
                     <th className="px-4 py-3 font-medium min-w-[300px]">搜索提问指令 (Prompt)</th>
                     <th className="px-4 py-3 font-medium w-16 text-center">操作</th>
                   </tr>
                 </thead>
                 <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                   {displayPrompts.map((item, index) => {
                      const globalIndex = flatPrompts.findIndex(p => p.id === item.id);
                      return (
                      <tr key={item.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-700/30 transition-colors">
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300 font-medium">{item.seed}</td>
                        <td className="px-4 py-3 text-slate-700 dark:text-slate-300">{item.persona}</td>
                        <td className="px-4 py-3 text-slate-500 dark:text-slate-400">{item.kw}</td>
                        <td className="px-4 py-3">
                           <input 
                              type="text" 
                              value={item.prompt} 
                              onChange={(e) => {
                                 if (globalIndex !== -1) {
                                    const newPrompts = [...flatPrompts];
                                    newPrompts[globalIndex].prompt = e.target.value;
                                    setFlatPrompts(newPrompts);
                                 }
                              }}
                              className="w-full bg-transparent border border-slate-200 dark:border-slate-600 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 dark:text-slate-100"
                           />
                        </td>
                        <td className="px-4 py-3 text-center">
                           <button onClick={() => {
                              if (globalIndex !== -1) {
                                 setFlatPrompts(flatPrompts.filter((_, i) => i !== globalIndex));
                              }
                           }} className="text-slate-400 hover:text-red-500 p-1 rounded-md transition-colors hover:bg-red-50 dark:hover:bg-red-900/20">
                              <Trash2 className="w-4 h-4 mx-auto" />
                           </button>
                        </td>
                      </tr>
                   )})}
                 </tbody>
               </table>
            </div>

            <div className="px-6 py-4 bg-white dark:bg-slate-800 flex justify-end gap-3 border-t border-gray-100 dark:border-slate-700">
              <button onClick={() => setShowReviewModal(false)} className="px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors border border-slate-200 dark:border-slate-600">取消</button>
              <button 
                onClick={() => {
                  localStorage.setItem(`intent_matrix_draft_${project.id}`, JSON.stringify({
                    expanded: expandedResults,
                    flatPrompts: flatPrompts
                  }));
                  setShowReviewModal(false);
                }}
                className="px-6 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-md transition-colors flex items-center"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                保存此维度修改 (暂存草稿)
              </button>
            </div>
          </div>
        </div>
      );})()}

      
      {expandedResults && !packageId && (
         <div className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-indigo-600 text-white px-6 py-3 rounded-full shadow-xl shadow-indigo-600/30 animate-in slide-in-from-bottom-8">
            <Activity className="w-5 h-5" />
            <span className="font-medium">全息矩阵已就绪，当前暂存 ({flatPrompts.length} 条探针)</span>
            <div className="w-px h-4 bg-indigo-400 mx-2"></div>
            <button className="flex items-center gap-2 text-sm hover:text-indigo-200 font-semibold" onClick={() => { setReviewFilter(null); setShowReviewModal(true); }}>
              全局预览修改 <Edit className="w-4 h-4" />
            </button>
            <div className="w-px h-4 bg-indigo-400 mx-2"></div>
            <button 
                onClick={handleSaveMatrix}
                disabled={isSaving || flatPrompts.length === 0}
                className="flex items-center gap-2 text-sm text-yellow-300 hover:text-yellow-100 font-bold disabled:opacity-50 transition-colors"
            >
              {isSaving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
              一键发射全盘至 3D 意图星图
            </button>
         </div>
      )}

      {expandedResults && packageId && (

         <div className="fixed bottom-8 left-1/2 -translate-x-1/2 flex items-center gap-3 bg-emerald-500 text-white px-6 py-3 rounded-full shadow-lg shadow-emerald-500/20 animate-in slide-in-from-bottom-8">
            <CheckCircle className="w-5 h-5" />
            <span className="font-medium">探针矩阵已生成，后台正在进行高维向量化映射... [词库 ID: {packageId}]</span>
            <div className="w-px h-4 bg-emerald-400 mx-2"></div>
            <button className="flex items-center gap-2 text-sm hover:text-emerald-100 font-semibold" onClick={() => window.location.href=`/prime/galaxy?projectId=${project?.id || ''}`}>
              进入 3D 星空视图 (聚类压缩) <Compass className="w-4 h-4" />
            </button>
         </div>
      )}

    </div>
  );
}
